import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import React from "react";
import type { ApiRow, ApiSuccess } from "@AxiosWrapper";
import type { Exact, ExactArrayItems } from "@Helpers/types/exact";
import type { SpaceDataItem } from "@ServerTypes/spaces/space.schema";

/**
 * The open project: the `/projects/getIncludedProjectsController` row `getCurrProjectData` stores, plus
 * two frontend-only members.
 */
export interface ProjectData extends ApiRow<"/projects/getIncludedProjectsController"> {
  isDataFetched: boolean;
  /** The cover-image URL `getCurrProjectData` builds when the row carries an `image` key. */
  projectImageUrl?: string | undefined;
}

/**
 * `Partial`, but every member may also be explicitly `undefined`. `Exact<Partial<T>, U>` maps each
 * member through `Exact<T[K] | undefined, …>` (reading an optional property always yields the
 * `undefined` arm), so its result cannot be assigned back into a plain `Partial<T>` under
 * `exactOptionalPropertyTypes`. Mirrors the same alias in `reducers/projectDashboard.ts`.
 */
type PartialOrUndefined<T> = { [K in keyof T]?: T[K] | undefined };

/**
 * Every member of the project record except `isDataFetched`, which the reducer defaults to `true`
 * when the payload omits it. (`PartialOrUndefined<ProjectData>` is what makes `isDataFetched`
 * optional here; the rest of its members are already covered by the `Omit`.)
 */
type SetProjectDataPayload = Omit<ProjectData, "isDataFetched"> & PartialOrUndefined<ProjectData>;

/**
 * The open space: the `/spaces/getSpaceById` payload `getCurrSpaceData` stores, or `{}` (nothing loaded, or the
 * read was refused). `PartialOrUndefined` rather than `Partial` so that, under `exactOptionalPropertyTypes`,
 * `setSpaceData`'s `Exact<>` constraint still proves its argument is one (see that alias).
 *
 * ⚠️ KNOWN BUG: `FileManagerNew.tsx` renders Forbidden only when this slice carries a `noAccess` flag, and
 * nothing sets one — `getCurrSpaceData` stores `{}` for a refused read — so a refused space renders blank.
 * Fix (behaviour change): declare a refusal member here, set it on `status: false`, and gate on it.
 */
type SpaceDataState = PartialOrUndefined<ApiSuccess<"/spaces/getSpaceById">["data"]>;

/** Breadcrumb item — text may be a string, translated string, or JSX element */
export interface BreadCrumbItem {
  text?: React.ReactNode | undefined;
  link?: string | undefined;
  showTooltip?: boolean | undefined;
  tooltipData?: string | undefined;
  template?: (() => React.ReactNode) | undefined;
}

/** No project loaded. Every row member is optional, so readers see none of them (`id` included) until a row lands. */
const initialProjectData: ProjectData = { isDataFetched: false };

const projectDataSlice = createSlice({
  name: "projectData",
  initialState: initialProjectData,
  reducers: {
    setProjectData: (_state: ProjectData, action: PayloadAction<SetProjectDataPayload>) => ({
      ...action.payload,
      isDataFetched: action.payload.isDataFetched ?? true,
    }),
  },
});

const allSpacesInProjectSlice = createSlice({
  name: "allSpacesInProject",
  initialState: [] as SpaceDataItem[],
  reducers: {
    setAllSpacesInProjects: (_state: SpaceDataItem[], action: PayloadAction<SpaceDataItem[]>) => action.payload,
  },
});

const spaceDataSlice = createSlice({
  name: "spaceData",
  initialState: {} as SpaceDataState,
  reducers: {
    setSpaceData: (_state: SpaceDataState, action: PayloadAction<SpaceDataState>) => action.payload,
  },
});

const breadCrumbDataSlice = createSlice({
  name: "breadCrumbData",
  initialState: [] as BreadCrumbItem[],
  reducers: {
    setBreadCrumbData: (_state: BreadCrumbItem[], action: PayloadAction<BreadCrumbItem[]>) => action.payload,
  },
});

// Internal raw action creators (Redux Toolkit auto-generated, no excess-property
// checking on variable arguments).
const { setProjectData: _setProjectDataRaw } = projectDataSlice.actions;
const { setAllSpacesInProjects: _setAllSpacesInProjectsRaw } = allSpacesInProjectSlice.actions;
const { setSpaceData: _setSpaceDataRaw } = spaceDataSlice.actions;
const { setBreadCrumbData: _setBreadCrumbDataRaw } = breadCrumbDataSlice.actions;

/**
 * Object-payload action creators are wrapped with `Exact<>` so excess properties are caught even when the
 * payload is passed through a variable (Redux Toolkit's auto-generated creators lose excess-property
 * checking once the literal is assigned to a variable).
 *
 * `Exact<>` is applied to the PARAMETER (`T & Exact<…>`) rather than used as the constraint — the same form
 * `reducers/fileSync.ts` and `reducers/measurements.ts` document. Under `exactOptionalPropertyTypes`
 * type-fest's `ExactObject` homomorphic mapping re-emits every optional member as `k?: V | undefined`, so a
 * bare `T extends Exact<SetProjectDataPayload, T>` no longer proves `T` is a `SetProjectDataPayload`.
 */
export const setProjectData = <T extends SetProjectDataPayload>(payload: T & Exact<SetProjectDataPayload, T>) =>
  _setProjectDataRaw(payload);

/**
 * `ExactArrayItems<>` works for HOMOGENEOUS arrays where every element shares
 * the same field set (entity lists). It runs per-element `Exact<>` checking,
 * catching excess properties even when the array is built via a variable.
 */
export const setAllSpacesInProjects = <T extends readonly SpaceDataItem[]>(
  payload: T & ExactArrayItems<SpaceDataItem, T>,
) => _setAllSpacesInProjectsRaw([...payload]);

export const setSpaceData = <T extends Exact<SpaceDataState, T>>(payload: T) => _setSpaceDataRaw(payload);

/**
 * `setBreadCrumbData` is intentionally NOT wrapped. Breadcrumb arrays are HETEROGENEOUS — different entries
 * omit different optional fields (some have `link`, some `template`, some only `text`).
 * `ExactArrayItems<>` infers the element type as a union of all sibling shapes and then forces every
 * element to match that union, which rejects legitimate use cases. For breadcrumb excess-property safety,
 * prefer the inline-array pattern at the call site, or `... satisfies BreadCrumbItem[]`.
 */
export const setBreadCrumbData = _setBreadCrumbDataRaw;

export const projectData = projectDataSlice.reducer;
export const allSpacesInProject = allSpacesInProjectSlice.reducer;
export const spaceData = spaceDataSlice.reducer;
export const breadCrumbData = breadCrumbDataSlice.reducer;

export default breadCrumbDataSlice.reducer;
